import {ErrorMessage} from 'formik';
import React from 'react';

import {FormContainer} from '../../form-containers/FormGroup';
import {FormSelectListGlobalStyle} from '../../form-control-features/FormSelectMenuList';
import {FormErrorMessage} from '../../form-labels/FormErrorMessage';
import {FormikAsyncSelect} from './FormikAsyncSelect';
import {RawAsyncSelect} from './RawAsyncSelect';

/**
 * @param {object} param0
 *
 * @param {any} [param0.cacheOptions]
 * If `cacheOptions` is truthy, then the loaded data will be cached. The cache
 * will remain until `cacheOptions` changes value.
 *
 * @param {boolean} [param0.closeMenuOnSelect]
 * Close the async select menu when the user selects an option
 *
 * @param {boolean} [param0.defaultOptions]
 * The default set of options to show before the user starts searching.
 * When set to `true`, the results for `loadOptions('')` will be autoloaded.
 *
 * @param  {{label: string; value: any} | Array<{label: string; value: any}>} [param0.defaultValue]
 * Specifies the initially selected option(s).
 *
 * @param {boolean} [param0.disabled]
 * Whether the async select is disabled.
 *
 * @param {React.ReactNode} [param0.statusBadge]
 * Specifies an extra label element to be displayed on the right side of the AsyncSelect.
 * Use the JSX syntax to include an icon element along with some text. For example: {<Icon name='edit' />} or {<>Custom text <Icon name='edit' /></>}
 *
 * @param {boolean} [param0.isClearable]
 * Specifies whether the value can be cleared.
 *
 * @param {boolean} [param0.isLoading]
 * Specifies whether the async select is in a loading state. Setting this to `true` will cause
 * the select to be displayed in the loading state, even if the Async select is not currently waiting
 * for `loadOptions` to resolve
 *
 * @param {boolean} [param0.isMulti]
 * Specifies whether the async select should allow multiple values to be selected.
 *
 * @param {React.ReactNode} [param0.label]
 * Label The text to be displayed as the label.
 *
 * @param {(<Value>(inputValue: string) => Promise<Array<{label: string; value: Value}>>) | (<Value>(inputValue: string, callback: (options: Array<{label: string; value: Value}>) => void) => void)} [param0.loadOptions]
 * This function should load the available options that are displayed to the user to click on. The current search input
 * is provided as a parameter. The function can either return a Promise that resolves to the array of options or it
 * can invoke the second (callback) parameter with the array of options.
 *
 * @param {<Value>(value: Value | Value[] | null) => Promise<{label: string; value: Value} | Array<{label: string; value: Value}> | null>} [param0.loadOptionsForValues]
 * *This prop is applicable only for the Formik version of the control.*
 *
 * If provided, this function will be invoked whenever the Formik value changes, whether by user action or programatically.
 * It will receive the currently selected **value** (or multiple **values** when `isMulti` is `true`) as an argument.
 * It should return a Promise that resolves to the option(s) corresponding to those values. The returned option(s) will be
 * displayed as selected in the `<AsyncSelect>` component.
 *
 * **Note: if this function returns a falsey value, the selection will be cleared in the UI, but the value within the Formik state will remain unchanged.**
 *
 * This function is useful when the form is being initialized with pre-selected values that are not included in the options loaded by `loadOptions`.
 *
 * @param {boolean} [param0.menuIsOpen]
 * Controls whether the dropdown menu is open (true) or closed (false) in AsyncSelect.
 *
 * @param {string} [param0.name]
 * *This prop is applicable only for the Formik version of the control.*
 *
 * The name of the field in the Formik context.
 *
 * Ensure to include the 'name' property for proper functioning with Formik-type form controls.
 * Wrap '<Formik></Formik>' around this and all other form control types for proper functionality.
 * Adding 'name' to a Formik AsyncSelect component is essential for linking the select input value to the Formik state.
 *
 * **Note: Formik is the default type injected into the form control.**
 *
 * @param {string} [param0.placeholder]
 * The text displayed in the AsyncSelect when it is empty and not focused, providing a hint to the user about what information is expected.
 * Placeholder text follows a naming convention for consistency:
 *   - 'Add filter'
 *   - 'Type your name'
 *   - 'Select an option'
 *   - 'Pick an option'
 *   - 'Fill in your name'
 *
 * Avoid using placeholders like:
 *   - '--- type ---'
 *   - '--- select ---'
 *
 * @param {React.ReactNode} [param0.prefix]
 * The text or icons to be displayed before the AsyncSelect. Icons can be added using the following format:
 *   `prefix={<><Icon name='edit' /> -- additional text --</>}`
 *
 * @param {boolean} [param0.raw]
 * If set to true, a non-Formik version of the form control will be injected instead.
 *
 * **Note: Formik is the default type injected into the form control.**
 *
 * @param {'sm'} [param0.size]
 * CSS variable names for adjusting the size of the form control you're using:
 *   - 'sm': Smaller variant of the form control type.
 *
 * @param {React.ReactNode} [param0.suffix]
 * The text or icons to be displayed after the AsyncSelect. Icons can be added using the following format:
 *   `suffix={<><Icon name='edit' /> -- additional text --</>}`
 *
 * @param {any} [param0.value]
 * *This prop is applicable only for the non-Formik version of the control.*
 *
 * The selected value, or multiple values if `isMulti` is set to `true`.
 * If provided, this function will be called when the user selects an option from the dropdown.
 *
 * @param {(option: {label: string; value: any} | Array<{label: string; value: any}> | null, action: import('react-select').ActionMeta<any>) => void} [param0.onChange]
 * Callback function to handle change events triggered by the async select component.
 *
 * The parameter can have one of the following values:
 * - `null` when `isClearable` is `true` and the user clears the value.
 * - A single option object when `isMulti` is `false`.
 * - An array of one or more option objects when `isMulti` is `true`.
 *
 * @example
 * ```jsx
 * <AsyncSelect
 *    key={reload}
 *    label="Name"
 *    loadOptions={promiseEntities}
 *    loadValueOption={loadValueOption}
 *    placeholder={disabledMessageText()}
 *    isClearable={isClearable}
 *    name={name.user}
 *    disabled={disabled}
 *    onChange={onChange}
 * />
 * ```
 */
export function AsyncSelect({
    cacheOptions,
    closeMenuOnSelect,
    defaultOptions,
    defaultValue,
    disabled,
    isClearable,
    isLoading,
    isMulti,
    key,
    label,
    loadOptions,
    loadOptionsForValues,
    menuIsOpen,
    name,
    placeholder,
    prefix,
    raw,
    size,
    statusBadge,
    suffix,
    validationError,
    value,
    onChange,
}) {
    const customComponents = {
        DropdownIndicator: null,
        IndicatorSeparator: null,
    };
    return (
        <>
            <FormSelectListGlobalStyle />
            <FormContainer
                disabled={disabled}
                errorMessage={
                    raw ? (
                        <>{validationError && <FormErrorMessage>{validationError}</FormErrorMessage>}</>
                    ) : (
                        <ErrorMessage name={name} component={FormErrorMessage} />
                    )
                }
                formSelect={true}
                label={label}
                prefix={prefix}
                size={size}
                suffix={suffix}
                statusBadge={statusBadge}>
                {raw ? (
                    <RawAsyncSelect
                        cacheOptions={cacheOptions}
                        closeMenuOnSelect={closeMenuOnSelect}
                        components={customComponents}
                        defaultOptions={defaultOptions}
                        defaultValue={defaultValue}
                        disabled={disabled}
                        isClearable={isClearable}
                        isLoading={isLoading}
                        isMulti={isMulti}
                        key={key}
                        loadOptions={loadOptions}
                        menuIsOpen={menuIsOpen}
                        menuPortalTarget={document.body}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                    />
                ) : (
                    <FormikAsyncSelect
                        cacheOptions={cacheOptions}
                        closeMenuOnSelect={closeMenuOnSelect}
                        components={customComponents}
                        defaultOptions={defaultOptions}
                        defaultValue={defaultValue}
                        disabled={disabled}
                        isClearable={isClearable}
                        isLoading={isLoading}
                        isMulti={isMulti}
                        key={key}
                        loadOptions={loadOptions}
                        loadOptionsForValues={loadOptionsForValues}
                        menuIsOpen={menuIsOpen}
                        menuPortalTarget={document.body}
                        name={name}
                        placeholder={placeholder}
                        onChange={onChange}
                    />
                )}
            </FormContainer>
        </>
    );
}
